import {
  type ChangeEvent,
  useEffect,
  useRef,
  useState
} from "react";
import {
  createProjectRender,
  createRoomRender,
  getRenderQuota,
  type RenderQuota,
  type ProjectRenderLighting
} from "../domain/renderRoom";

const MAX_PHOTO_SIZE = 15 * 1024 * 1024;

type RenderMode = "project" | "room";

type Props = {
  open: boolean;
  projectName: string;
  projectSpecification: string;
  moduleCount: number;
  getProjectRenderImage: () => string | null;
  getProjectRenderReferenceImage: () => string | null;
  getProjectRenderStateSpecification: () => string;
  onAttachResult: (result: Blob, fileName: string) => void;
  /** Визуализатор открыт из публикации модели: рендер можно поставить обложкой (№ 29). */
  onUseAsCover?: (result: Blob) => Promise<void>;
  onRequestDesigner: () => void;
  onClose: () => void;
};

const fileLabel = (file: File) => {
  const megabytes = file.size / 1024 / 1024;
  return `${file.name} · ${megabytes.toFixed(megabytes >= 10 ? 0 : 1)} МБ`;
};

const moduleWord = (count: number) => {
  const lastTwo = count % 100;
  const last = count % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return "модулей";
  if (last === 1) return "модуль";
  if (last >= 2 && last <= 4) return "модуля";
  return "модулей";
};

const renderWord = (count: number) => {
  const lastTwo = count % 100;
  const last = count % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return "визуализаций";
  if (last === 1) return "визуализация";
  if (last >= 2 && last <= 4) return "визуализации";
  return "визуализаций";
};

function RenderComparison({
  before,
  after,
  aspectRatio
}: {
  before: string;
  after: string;
  aspectRatio: number;
}) {
  const [position, setPosition] = useState(50);

  useEffect(() => setPosition(50), [after]);

  return (
    <div
      className="room-render-comparison"
      style={{
        aspectRatio: String(aspectRatio),
        width: `min(100%, ${aspectRatio * 62}vh)`
      }}
    >
      <img src={after} alt="Визуализация мебели в комнате" />
      <div
        className="room-render-comparison__before"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
      >
        <img src={before} alt="Исходная фотография комнаты" />
      </div>
      <span className="room-render-comparison__label is-before">До</span>
      <span className="room-render-comparison__label is-after">После</span>
      <span
        className="room-render-comparison__divider"
        style={{ left: `${position}%` }}
        aria-hidden="true"
      >
        <i>↔</i>
      </span>
      <input
        type="range"
        min="0"
        max="100"
        value={position}
        aria-label="Сравнить фотографию до и после"
        onChange={(event) => setPosition(Number(event.target.value))}
      />
    </div>
  );
}

export function RoomRenderDialog({
  open,
  projectName,
  projectSpecification,
  moduleCount,
  getProjectRenderImage,
  getProjectRenderReferenceImage,
  getProjectRenderStateSpecification,
  onAttachResult,
  onUseAsCover,
  onRequestDesigner,
  onClose
}: Props) {
  const [coverBusy, setCoverBusy] = useState(false);
  const [mode, setMode] = useState<RenderMode>("project");
  const [projectPreview, setProjectPreview] = useState("");
  const [lighting, setLighting] =
    useState<ProjectRenderLighting>("day");
  const [roomPhoto, setRoomPhoto] = useState<File | null>(null);
  const [roomPreview, setRoomPreview] = useState("");
  const [roomAspectRatio, setRoomAspectRatio] = useState(4 / 3);
  const [resultUrl, setResultUrl] = useState("");
  const [resultBlob, setResultBlob] = useState<Blob | null>(null);
  const [resultAttached, setResultAttached] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [quota, setQuota] = useState<RenderQuota | null>(null);
  const [quotaLoading, setQuotaLoading] = useState(false);
  /** Выбор ракурса (11.10, ai-01): окно прячется, 3D свободно, «Готово» снимает кадр. */
  const [pickingAngle, setPickingAngle] = useState(false);
  // На телефоне на время выбора ракурса 3D растёт почти на весь экран (12.10).
  useEffect(() => {
    if (!pickingAngle) return;
    document.documentElement.classList.add("is-picking-angle");
    window.scrollTo({ top: 0 });
    return () => document.documentElement.classList.remove("is-picking-angle");
  }, [pickingAngle]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(
    () => () => {
      if (roomPreview) URL.revokeObjectURL(roomPreview);
    },
    [roomPreview]
  );

  useEffect(
    () => () => {
      if (resultUrl) URL.revokeObjectURL(resultUrl);
    },
    [resultUrl]
  );

  // Кадр 3D для окна: функция приходит из App новой при каждой его перерисовке,
  // поэтому держим её в ref — иначе эффект перезапускался и отменял кадр,
  // и «Вся композиция» оставалась «Готовим полный проект…» (приёмка 12.10, ai-03).
  const projectImageRef = useRef(getProjectRenderImage);
  projectImageRef.current = getProjectRenderImage;
  useEffect(() => {
    if (!open) return;
    let done = false;
    const take = () => {
      if (done) return;
      done = true;
      const snapshot = projectImageRef.current();
      if (snapshot) {
        setProjectPreview(snapshot);
        setError("");
      } else {
        setError("3D-сцена ещё загружается. Попробуйте ещё раз.");
      }
    };
    const frame = window.requestAnimationFrame(take);
    // Вкладка в фоне не рисует кадры — снимок всё равно сделаем.
    const fallback = window.setTimeout(take, 400);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(fallback);
    };
  }, [open, mode]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setQuotaLoading(true);
    getRenderQuota()
      .then((nextQuota) => {
        if (active) setQuota(nextQuota);
      })
      .catch((quotaError) => {
        console.error("Unable to load render quota", quotaError);
        if (active) {
          setQuota(null);
          setError("Не удалось проверить доступ к визуализации. Попробуйте ещё раз.");
        }
      })
      .finally(() => {
        if (active) setQuotaLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    const refresh = () => {
      getRenderQuota()
        .then((nextQuota) => {
          if (active) setQuota(nextQuota);
        })
        .catch((quotaError) => {
          console.error("Unable to refresh render quota", quotaError);
        });
    };

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };

    const interval = window.setInterval(refresh, 6000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [open]);

  if (!open) return null;

  const clearResult = () => {
    if (resultUrl) URL.revokeObjectURL(resultUrl);
    setResultUrl("");
    setResultBlob(null);
    setResultAttached(false);
  };

  const setGeneratedResult = (result: Blob) => {
    clearResult();
    setResultBlob(result);
    setResultUrl(URL.createObjectURL(result));
  };

  const selectMode = (nextMode: RenderMode) => {
    if (nextMode === mode || generating) return;
    clearResult();
    setError("");
    setMode(nextMode);
  };

  const refreshProjectPreview = () => {
    const snapshot = getProjectRenderImage();
    if (!snapshot) {
      setError("3D-сцена ещё загружается. Попробуйте ещё раз.");
      return null;
    }
    clearResult();
    setProjectPreview(snapshot);
    setError("");
    return snapshot;
  };

  const selectPhoto = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Выберите фотографию JPG, PNG или WebP.");
      return;
    }
    if (file.size > MAX_PHOTO_SIZE) {
      setError("Фотография должна быть не больше 15 МБ.");
      return;
    }

    if (roomPreview) URL.revokeObjectURL(roomPreview);
    clearResult();
    const preview = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      setRoomAspectRatio(
        image.naturalWidth / Math.max(1, image.naturalHeight)
      );
    };
    image.src = preview;
    setRoomPhoto(file);
    setRoomPreview(preview);
    setError("");
  };

  const generateProject = async () => {
    const projectImage = projectPreview || refreshProjectPreview();
    if (!projectImage) return;
    const projectReferenceImage = getProjectRenderReferenceImage();
    const projectRenderState = getProjectRenderStateSpecification();
    if (!projectReferenceImage || !projectRenderState.includes("DOOR POSE LOCK")) {
      throw new Error(
        "Не удалось подготовить технический кадр и положение дверей. Обновите страницу сочетанием Ctrl+F5 и попробуйте ещё раз. Лимит визуализаций не списан."
      );
    }
    const renderSpecification = [
      projectRenderState,
      projectSpecification
    ]
      .filter(Boolean)
      .join("\n");
    const result = await createProjectRender({
      projectImage,
      projectReferenceImage,
      projectName,
      projectSpecification: renderSpecification,
      lighting
    });
    setGeneratedResult(result);
  };

  const generateRoom = async () => {
    if (!roomPhoto) return;
    const projectImage = projectPreview || refreshProjectPreview();
    if (!projectImage) return;
    const renderSpecification = [
      getProjectRenderStateSpecification(),
      projectSpecification
    ]
      .filter(Boolean)
      .join("\n");
    const result = await createRoomRender({
      roomPhoto,
      projectImage,
      projectName,
      projectSpecification: renderSpecification,
      moduleCount,
      roomAspectRatio
    });
    setGeneratedResult(result);
  };

  const generate = async () => {
    if (generating) return;
    setGenerating(true);
    setError("");
    try {
      if (mode === "project") {
        await generateProject();
      } else {
        await generateRoom();
      }
      try {
        setQuota(await getRenderQuota());
      } catch (quotaError) {
        console.error("Unable to refresh render quota", quotaError);
      }
    } catch (renderError) {
      setError(
        renderError instanceof Error
          ? renderError.message
          : "Не удалось создать визуализацию."
      );
      try {
        setQuota(await getRenderQuota());
      } catch (quotaError) {
        console.error("Unable to refresh render quota", quotaError);
      }
    } finally {
      setGenerating(false);
    }
  };

  const fileName = () => {
    const suffix =
      mode === "project" ? "фоторендер проекта" : "визуализация в комнате";
    return `Egel Design — ${projectName || "проект"} — ${suffix}.jpg`;
  };

  const download = () => {
    if (!resultUrl) return;
    const link = document.createElement("a");
    link.href = resultUrl;
    link.download = fileName();
    link.click();
  };

  const attachResult = () => {
    if (!resultBlob) return;
    onAttachResult(resultBlob, fileName());
    setResultAttached(true);
  };

  const projectMode = mode === "project";
  // Кадр композиции снимается и в момент нажатия (generateRoom), поэтому кнопка
  // ждёт только фото и изделие, а не готового превью (12.10, ai-03).
  const sourceReady = projectMode
    ? Boolean(projectPreview)
    : Boolean(roomPhoto && moduleCount > 0);
  const canGenerate = sourceReady && Boolean(quota?.canRender) && !quotaLoading;

  const quotaContent = (() => {
    if (quotaLoading) {
      return {
        title: "Проверяем доступ…",
        text: "Это займёт всего секунду."
      };
    }
    if (!quota) {
      return {
        title: "Не удалось проверить доступ",
        text: "Обновите окно и попробуйте ещё раз."
      };
    }
    if (quota.staff && quota.reason === "available") {
      return {
        title: "Без лимита — вы в кабинете сотрудника",
        text: "Делайте сколько нужно вариантов: для проверки, презентации или обложки модели."
      };
    }
    if (quota.reason === "order_required") {
      return {
        title: "Бесплатная визуализация готова",
        text: `Отправьте проект дизайнеру. После проверки он откроет ещё ${quota.bonusLimit} ${renderWord(quota.bonusLimit)}.`
      };
    }
    if (quota.reason === "approval_pending") {
      return {
        title: "Проект ожидает проверки",
        text: `После подтверждения дизайнером здесь автоматически откроются ещё ${quota.bonusLimit} ${renderWord(quota.bonusLimit)}.`
      };
    }
    if (quota.reason === "approval_rejected") {
      return {
        title: "Нужно уточнить заявку",
        text: "Проверьте контактные данные и отправьте проект дизайнеру ещё раз."
      };
    }
    if (quota.reason === "exhausted") {
      return {
        title: "Доступные варианты использованы",
        text: "Дизайнер уже получил проект и поможет подготовить следующий вариант."
      };
    }
    if (quota.reason === "daily_limit") {
      return {
        title: "Лимит на сегодня достигнут",
        text: "Новые варианты снова будут доступны завтра."
      };
    }
    if (quota.reason === "service_limit") {
      return {
        title: "Небольшая техническая пауза",
        text: "Попробуйте создать визуализацию немного позже."
      };
    }
    if (quota.reason === "busy") {
      return {
        title: "Визуализация уже создаётся",
        text: "Дождитесь готового изображения."
      };
    }
    if (quota.orderSubmitted) {
      return {
        title: `Доступно ещё ${quota.remaining} ${renderWord(quota.remaining)}`,
        text: "Дополнительные варианты открыты дизайнером после проверки проекта."
      };
    }
    return {
      title: "Первая визуализация — бесплатно",
      text: "Без регистрации и ввода контактных данных."
    };
  })();

  /** Почему кнопка неактивна — строкой под ней, чтобы не гадать (ai-03). */
  const blockedReason = generating || canGenerate ? ""
    : quotaLoading ? "Проверяем доступ к визуализации…"
      : !projectMode && !moduleCount ? "Сначала добавьте изделие в проект."
        : !projectMode && !roomPhoto ? "Загрузите фото комнаты — кнопка станет активной."
          : projectMode && !projectPreview ? "Готовим кадр 3D — нажмите «Выбрать ракурс», если кадр не появился."
            : !quota ? "Не удалось проверить доступ. Закройте и откройте окно ещё раз."
              : !quota.canRender ? `${quotaContent.title}. ${quotaContent.text}`
                : "";

  if (pickingAngle) {
    return <div className="render-angle-bar" role="dialog" aria-label="Выбор ракурса">
      <span><strong>Выберите ракурс</strong>
        <small>Поверните и приблизьте 3D пальцем или мышью, как изделие должно выглядеть на визуализации.</small></span>
      <button type="button" className="primary-button" onClick={() => {
        setPickingAngle(false);
        refreshProjectPreview();
      }}>Готово</button>
      <button type="button" className="secondary-button" onClick={() => setPickingAngle(false)}>Отмена</button>
    </div>;
  }

  return (
    <div
      className="dialog-backdrop room-render-backdrop"
      role="presentation"
      onMouseDown={() => !generating && onClose()}
    >
      <section
        className="room-render-dialog"
        aria-busy={generating || quotaLoading}
        role="dialog"
        aria-modal="true"
        aria-labelledby="room-render-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          className="dialog-close"
          type="button"
          aria-label="Закрыть"
          disabled={generating}
          onClick={onClose}
        >
          <svg
            aria-hidden="true"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
          >
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>

        <header className="room-render-dialog__header">
          <span className="eyebrow">AI-визуализация</span>
          <h2 id="room-render-title">Посмотрите проект до заказа</h2>
          <p>
            Создайте каталожный фоторендер или перенесите всю собранную
            композицию в фотографию своей комнаты.
          </p>
        </header>

        <div className="room-render-modes" role="tablist" aria-label="Тип рендера">
          <button
            className={projectMode ? "is-selected" : ""}
            type="button"
            role="tab"
            aria-selected={projectMode}
            disabled={generating}
            onClick={() => selectMode("project")}
          >
            <span>✦</span>
            <strong>Фоторендер проекта</strong>
            <small>Улучшить текущий 3D-ракурс</small>
          </button>
          <button
            className={!projectMode ? "is-selected" : ""}
            type="button"
            role="tab"
            aria-selected={!projectMode}
            disabled={generating}
            onClick={() => selectMode("room")}
          >
            <span>⌂</span>
            <strong>В моей комнате</strong>
            <small>Перенести весь проект на фото</small>
          </button>
        </div>

        <div className="room-render-workspace">
          <div className="room-render-settings">
            {projectMode ? (
              <>
                <div
                  className={`project-render-source${
                    projectPreview ? " has-preview" : ""
                  }`}
                >
                  {projectPreview ? (
                    <img
                      src={projectPreview}
                      alt="Текущий чистый ракурс проекта"
                    />
                  ) : (
                    <span>Готовим текущий 3D-ракурс…</span>
                  )}
                  <small>Размерные линии скрываются автоматически</small>
                </div>
                <button
                  className="project-render-refresh"
                  type="button"
                  disabled={generating}
                  onClick={() => setPickingAngle(true)}
                >
                  Выбрать ракурс
                </button>

                <label className="room-render-field">
                  <span>Освещение</span>
                  <select
                    value={lighting}
                    disabled={generating}
                    onChange={(event) =>
                      setLighting(
                        event.target.value as ProjectRenderLighting
                      )
                    }
                  >
                    <option value="day">Мягкий дневной свет</option>
                    <option value="sunny">Солнечный день</option>
                    <option value="evening">Тёплый вечер</option>
                  </select>
                </label>

                <div className="room-render-note">
                  <span>Перед генерацией</span>
                  <p>
                    Нажмите «Выбрать ракурс» над кадром, поверните 3D и нажмите
                    «Готово». AI улучшит материалы, свет, тени и отражения.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="room-render-steps">
                  <div className={roomPhoto ? "is-complete" : "is-current"}>
                    <span>1</span>
                    <p>
                      <strong>Фотография комнаты</strong>
                      <small>Горизонтально, прямо и без людей</small>
                    </p>
                  </div>
                  <div className={roomPhoto ? "is-current" : undefined}>
                    <span>2</span>
                    <p>
                      <strong>Единый фоторендер</strong>
                      <small>С естественным светом и тенями</small>
                    </p>
                  </div>
                </div>

                <button
                  className={`room-photo-picker${
                    roomPreview ? " has-photo" : ""
                  }`}
                  type="button"
                  disabled={generating}
                  onClick={() => inputRef.current?.click()}
                >
                  {roomPreview ? (
                    <img src={roomPreview} alt="Загруженная комната" />
                  ) : (
                    <span className="room-photo-picker__empty">
                      <strong>Загрузить фото комнаты</strong>
                      <small>Горизонтальный кадр · JPG, PNG или WebP</small>
                    </span>
                  )}
                  {roomPhoto && (
                    <span className="room-photo-picker__label">
                      {fileLabel(roomPhoto)}
                    </span>
                  )}
                </button>
                <input
                  ref={inputRef}
                  className="room-photo-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={selectPhoto}
                />

                <div className="room-project-reference">
                  {projectPreview ? (
                    <img src={projectPreview} alt="Полная композиция проекта" />
                  ) : (
                    <span>Готовим полный проект…</span>
                  )}
                  <div>
                    <strong>Вся композиция</strong>
                    <small>
                      {moduleCount} {moduleWord(moduleCount)} · без выбора стены
                    </small>
                  </div>
                  {/* Как в фоторендере (11.10, ai-03): «Обновить» снимал тот же кадр,
                      потому что окно закрывает 3D и ракурс не поменять. */}
                  <button
                    type="button"
                    disabled={generating}
                    onClick={() => setPickingAngle(true)}
                  >
                    Выбрать ракурс
                  </button>
                </div>

                <div className="room-render-note">
                  <span>Как сфотографировать комнату</span>
                  <p>
                    Снимайте горизонтально, по возможности прямо и при хорошем
                    свете. Оставьте в кадре свободную стену и участок пола под
                    мебель, уберите людей и крупные предметы перед местом
                    установки. Затем выставьте 3D примерно под тем же углом.
                  </p>
                </div>
              </>
            )}

            {error && (
              <div className="dialog-error" role="alert">
                {error}
              </div>
            )}

            <div
              className={`render-quota-card${
                quota?.reason === "order_required" ||
                quota?.reason === "approval_rejected"
                  ? " needs-order"
                  : quota?.reason === "approval_pending"
                    ? " is-pending"
                    : ""
              }`}
            >
              <span aria-hidden="true">✦</span>
              <div>
                <strong>{quotaContent.title}</strong>
                <small>{quotaContent.text}</small>
              </div>
              {(quota?.reason === "order_required" ||
                quota?.reason === "approval_rejected") && (
                <button type="button" onClick={onRequestDesigner}>
                  {quota.reason === "approval_rejected"
                    ? "Отправить повторно"
                    : "Отправить проект"}
                </button>
              )}
            </div>

            <button
              className="primary-button full room-render-generate"
              type="button"
              disabled={!canGenerate || generating}
              onClick={generate}
            >
              {generating
                ? "Создаём визуализацию…"
                : projectMode
                  ? "Создать фоторендер"
                  : "Создать вариант в комнате"}
            </button>
            {blockedReason && <small className="room-render-blocked">{blockedReason}</small>}
          </div>

          <div
            className={`room-render-result${resultUrl ? " has-result" : ""}${
              !projectMode && roomPreview && !resultUrl
                ? " has-sources"
                : ""
            }`}
            aria-live="polite"
          >
            {generating ? (
              <div className="room-render-loading">
                <span />
                <strong>
                  {projectMode
                    ? "Проявляем реалистичные материалы"
                    : "Собираем проект внутри комнаты"}
                </strong>
              </div>
            ) : resultUrl ? (
              <>
                {!projectMode && roomPreview ? (
                  <RenderComparison
                    before={roomPreview}
                    after={resultUrl}
                    aspectRatio={roomAspectRatio}
                  />
                ) : (
                  <img src={resultUrl} alt="Фоторендер проекта" />
                )}
                <div className="room-render-result__actions">
                  <button type="button" onClick={download}>
                    Скачать
                  </button>
                  <button
                    type="button"
                    className={resultAttached ? "is-attached" : ""}
                    onClick={attachResult}
                  >
                    {resultAttached
                      ? "Добавлено к заявке"
                      : "Добавить к заявке"}
                  </button>
                  {onUseAsCover && resultBlob && <button
                    type="button"
                    className="room-render-result__cover"
                    disabled={coverBusy}
                    onClick={async () => {
                      setCoverBusy(true);
                      try { await onUseAsCover(resultBlob); } finally { setCoverBusy(false); }
                    }}
                  >
                    {coverBusy ? "Готовлю обложку…" : "Поставить обложкой модели"}
                  </button>}
                  <button
                    type="button"
                    disabled={!canGenerate}
                    onClick={generate}
                  >
                    Создать другой
                  </button>
                </div>
              </>
            ) : !projectMode && roomPreview ? (
              <div className="room-render-sources">
                <div>
                  <span>1 · Реальная комната</span>
                  <img src={roomPreview} alt="Фотография комнаты" />
                </div>
                <i aria-hidden="true">＋</i>
                <div>
                  <span>2 · Весь 3D-проект</span>
                  {projectPreview ? (
                    <img src={projectPreview} alt="Вся композиция проекта" />
                  ) : (
                    <strong>Готовим 3D…</strong>
                  )}
                </div>
                <p>
                  AI сохранит комнату из первого кадра и перенесёт в неё всю
                  мебель из второго: у стен, в углах и в центре одновременно.
                </p>
              </div>
            ) : (
              <div className="room-render-placeholder">
                <span>✦</span>
                <strong>
                  {projectMode
                    ? "Здесь появится фоторендер"
                    : "Загрузите фотографию комнаты"}
                </strong>
                <p>
                  {projectMode
                    ? "Геометрия и текущий ракурс берутся непосредственно из 3D-конфигуратора."
                    : "Снимайте горизонтально и оставьте свободное место у стены и на полу для установки мебели."}
                </p>
              </div>
            )}
          </div>
        </div>

      </section>
    </div>
  );
}
